"use client";

import React, { useEffect, useRef, useState } from "react";
import { Crosshair, Magnet, ShieldCheck, Zap } from "lucide-react";
const steps = [{
  title: "Setup",
  description: "We architect precision-targeted campaigns tailored to your specific home improvement niche, establishing the data foundation.",
  icon: Crosshair
}, {
  title: "Capture",
  description: "Deploying high-converting assets to attract and capture exclusive, high-intent prospects actively seeking your services.",
  icon: Magnet
}, {
  title: "Verify",
  description: "Rigorous multi-point verification ensures only qualified, actionable leads pass through our quality control system.",
  icon: ShieldCheck
}, {
  title: "Deliver",
  description: "Seamless, real-time routing directly to your sales team or CRM for immediate action and maximum conversion.",
  icon: Zap
}];
export default function OurMethodology() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const triggerPoint = windowHeight * 0.5;
      const topOffset = rect.top - triggerPoint;
      const scrollableHeight = rect.height * 0.8;
      let percentage = -topOffset / scrollableHeight * 100;
      percentage = Math.max(0, Math.min(100, percentage));
      setProgress(percentage);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="our-methodology" className="dark bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 uppercase">
            Our <span className="text-primary">Methodology</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            A precision-engineered process designed to cut through the noise and deliver high-intent prospects directly to your pipeline.
          </p>
        </div>

        <div ref={containerRef} className="relative max-w-4xl mx-auto py-10">
          {}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-border -translate-x-1/2" />
          
          {}
          <div className="absolute left-8 md:left-1/2 top-0 w-0.5 bg-primary -translate-x-1/2 transition-all duration-150 ease-out shadow-[0_0_15px_var(--color-primary)]" style={{
          height: `${progress}%`
        }} />

          <div className="flex flex-col gap-16 md:gap-24">
            {steps.map((step, i) => {
            const activationPoint = i / (steps.length - 1) * 100;
            const isActive = progress >= activationPoint - 10;
            return <div key={i} data-index={i} className={`relative flex flex-col md:flex-row items-start md:items-center justify-between group ${i % 2 === 0 ? "md:flex-row-reverse" : ""}`}>
                  {}
                  <div className={`absolute left-8 md:left-1/2 w-12 h-12 rounded-none border-2 flex items-center justify-center -translate-x-1/2 z-10 transition-all duration-500 bg-background ${isActive ? "border-primary text-primary shadow-[0_0_20px_var(--color-primary)] scale-110" : "border-border text-muted-foreground"}`}>
                    <span className="font-mono font-bold text-lg">
                      0{i + 1}
                    </span>
                  </div>

                  {}
                  <div className={`w-full pl-24 md:pl-0 md:w-[calc(50%-4rem)] transition-all duration-700 ${isActive ? "opacity-100 translate-y-0" : "opacity-40 translate-y-8"} ${i % 2 === 0 ? "md:text-right" : "md:text-left"}`}>
                    <div className={`bg-card/30 backdrop-blur-sm border p-8 transition-colors duration-300 ${isActive ? "border-primary/50" : "border-border/50"}`}>
                      <div className={`flex items-center gap-4 mb-4 ${i % 2 === 0 ? "md:flex-row-reverse" : ""}`}>
                        <div className={`p-3 bg-background border transition-colors duration-300 ${isActive ? "border-primary/50 text-primary shadow-[0_0_10px_var(--color-primary)]" : "border-border text-muted-foreground"}`}>
                          <step.icon className="w-6 h-6" />
                        </div>
                        <h3 className="text-2xl font-bold tracking-tight uppercase">
                          {step.title}
                        </h3>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}